/**
 * Safe Storage - Wrapper para localStorage/sessionStorage
 * Usa fallback em memória quando storage está bloqueado (Safari privado, PCs corporativos)
 */

// Fallback em memória quando storage bloqueado
const memoryLocalStorage = new Map<string, string>();
const memorySessionStorage = new Map<string, string>();

let localStorageAvailable: boolean | null = null;
let sessionStorageAvailable: boolean | null = null;

function isLocalStorageAvailable(): boolean {
  if (localStorageAvailable !== null) return localStorageAvailable;
  
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    localStorageAvailable = true;
  } catch {
    localStorageAvailable = false;
    console.warn('[SafeStorage] localStorage indisponível, usando fallback em memória');
  }
  return localStorageAvailable;
}

function isSessionStorageAvailable(): boolean {
  if (sessionStorageAvailable !== null) return sessionStorageAvailable;
  
  try {
    const testKey = '__storage_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    sessionStorageAvailable = true;
  } catch {
    sessionStorageAvailable = false;
    console.warn('[SafeStorage] sessionStorage indisponível, usando fallback em memória');
  }
  return sessionStorageAvailable;
}

export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return memoryLocalStorage.get(key) ?? null;
  },
  
  setItem: (key: string, value: string): void => {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
    } else {
      memoryLocalStorage.set(key, value);
    }
  },
  
  removeItem: (key: string): void => {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    } else {
      memoryLocalStorage.delete(key);
    }
  },
  
  clear: (): void => {
    if (isLocalStorageAvailable()) {
      localStorage.clear();
    } else {
      memoryLocalStorage.clear();
    }
  },

  // Para compatibilidade com Supabase auth storage
  get length(): number {
    if (isLocalStorageAvailable()) {
      return localStorage.length;
    }
    return memoryLocalStorage.size;
  },

  key: (index: number): string | null => {
    if (isLocalStorageAvailable()) {
      return localStorage.key(index);
    }
    const keys = Array.from(memoryLocalStorage.keys());
    return keys[index] ?? null;
  },
};

export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    if (isSessionStorageAvailable()) {
      return sessionStorage.getItem(key);
    }
    return memorySessionStorage.get(key) ?? null;
  },
  
  setItem: (key: string, value: string): void => {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem(key, value);
    } else {
      memorySessionStorage.set(key, value);
    }
  },
  
  removeItem: (key: string): void => {
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem(key);
    } else {
      memorySessionStorage.delete(key);
    }
  },
  
  clear: (): void => {
    if (isSessionStorageAvailable()) {
      sessionStorage.clear();
    } else {
      memorySessionStorage.clear();
    }
  },

  get length(): number {
    if (isSessionStorageAvailable()) {
      return sessionStorage.length;
    }
    return memorySessionStorage.size;
  },

  key: (index: number): string | null => {
    if (isSessionStorageAvailable()) {
      return sessionStorage.key(index);
    }
    const keys = Array.from(memorySessionStorage.keys());
    return keys[index] ?? null;
  },
};

// Verificação de disponibilidade para UI
export function isStorageAvailable(): boolean {
  return isLocalStorageAvailable();
}
